import { useEffect, useMemo, useState } from "react";
import { Alert, Breadcrumb, Button, Card, Descriptions, Empty, List, Space, Steps, Tabs, Tag } from "antd";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useQuery } from "../../common/utils";
import projectDefault from "../../img/project_default.jpg";
import { fetchMySaleActivityAuditInfo } from "./api";
import { getSaleActivityAuditState, getSaleActivityTimeStatusText } from "./status";
import { SaleActivityInfo, SaleAttachment } from "./types";
import "./activity.scss";

function textOrDash(value?: string | number) {
  return value || "--";
}

function getFileNameFromUrl(url: string, fallback: string) {
  const pureUrl = url.split("?")[0];
  try {
    return decodeURIComponent(pureUrl.split("/").filter(Boolean).pop() || fallback);
  } catch {
    return pureUrl.split("/").filter(Boolean).pop() || fallback;
  }
}

function buildFileAttachment(url?: string, fallback = "附件"): SaleAttachment[] {
  if (!url) return [];
  return [{
    fileName: getFileNameFromUrl(url, fallback),
    fileUrl: url,
    url,
  }];
}

function uniqueAttachments(list?: SaleAttachment[]) {
  const seen = new Set<string>();
  return (list || []).filter((item) => {
    const key = item.id ? String(item.id) : item.fullPathFile || item.fileUrl || item.url || item.shortPathFile || item.fileName || item.name || "";
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function AttachmentList({ list }: { list?: SaleAttachment[] }) {
  const data = uniqueAttachments(list);
  if (data.length === 0) return null;
  return (
    <List
      className="sale-activity-audit-attachments"
      dataSource={data}
      locale={{ emptyText: "暂无附件" }}
      renderItem={(item, index) => {
        const url = item.fullPathFile || item.fileUrl || item.url || "";
        return (
          <List.Item key={item.id || index}>
            <div className="sale-activity-audit-attachment">
              <a href={url} target="_blank" rel="noreferrer">
                <DownloadOutlined /> {item.fileName || item.name || `附件${index + 1}`}
              </a>
              {item.fileSize ? <span>{Math.ceil(item.fileSize / 1024 / 1024)}M</span> : null}
            </div>
          </List.Item>
        );
      }}
    />
  );
}

export default function SaleActivityAuditDetail() {
  const query = useQuery();
  const activityId = query.get("activityId") || "";
  const type = (query.get("type") || "user") as "expert" | "user";
  const [info, setInfo] = useState<SaleActivityInfo | null>(null);

  useEffect(() => {
    if (!activityId) return;
    fetchMySaleActivityAuditInfo(type, activityId).then((res) => {
      setInfo(res.data?.activityInfo || res.data || null);
    });
  }, [activityId, type]);

  const auditTitle = type === "expert" ? "专家报名审批" : "学员报名审批";
  const roleText = type === "expert" ? "专家" : "学员";
  const submitTime = type === "expert" ? info?.expertSubmitTime : info?.userSubmitTime;
  const auditTime = type === "expert" ? info?.expertAuditTime : info?.userAuditTime;
  const auditState = getSaleActivityAuditState(info, type);
  const statusText = auditState.text;
  const rejectReason = type === "expert" ? info?.expertAuditRejectReason : info?.userAuditRejectReason;
  const isRejected = auditState.rejected;
  const isPassed = auditState.passed;
  const isPending = auditState.pending;
  const statusTagColor = isPassed ? "green" : isRejected ? "red" : "blue";
  const ResultIcon = isPassed ? CheckCircleFilled : isRejected ? CloseCircleFilled : ClockCircleFilled;
  const currentRoleItems = type === "expert"
    ? [
      { label: "计划招募专家", value: `${textOrDash(info?.signUpExpertNum)}人` },
      { label: "专家招募时间", value: `${textOrDash(info?.expertHireBeginTime)} 至 ${textOrDash(info?.expertHireEndTime)}` },
      { label: "专家招募状态", value: getSaleActivityTimeStatusText(info, "expert") },
    ]
    : [
      { label: "计划招募学员", value: `${textOrDash(info?.signUpUserNum || info?.signUpNum)}人` },
      { label: "学员招募时间", value: `${textOrDash(info?.signUpBeginTime)} 至 ${textOrDash(info?.signUpEndTime)}` },
      { label: "学员招募状态", value: getSaleActivityTimeStatusText(info, "user") },
    ];
  const otherRoleItems = type === "expert"
    ? [
      { label: "计划招募学员", value: `${textOrDash(info?.signUpUserNum || info?.signUpNum)}人` },
      { label: "学员招募时间", value: `${textOrDash(info?.signUpBeginTime)} 至 ${textOrDash(info?.signUpEndTime)}` },
      { label: "学员招募状态", value: getSaleActivityTimeStatusText(info, "user") },
    ]
    : [
      { label: "计划招募专家", value: `${textOrDash(info?.signUpExpertNum)}人` },
      { label: "专家招募时间", value: `${textOrDash(info?.expertHireBeginTime)} 至 ${textOrDash(info?.expertHireEndTime)}` },
      { label: "专家招募状态", value: getSaleActivityTimeStatusText(info, "expert") },
    ];
  const contentTabs = useMemo(() => [
    {
      key: "content",
      label: "活动介绍",
      html: info?.activityContent || "暂无活动介绍",
      attachments: info?.saleAttachmentList || [],
    },
    {
      key: "expert",
      label: "专家招募通知",
      html: info?.expertHireContent || "暂无专家招募通知",
      attachments: [
        ...buildFileAttachment(info?.expertHireFile, "专家招募附件"),
        ...(info?.saleExpertAttachmentList || []),
      ],
    },
    {
      key: "user",
      label: "学员招募通知",
      html: info?.userHireContent || "暂无学员招募通知",
      attachments: [],
    },
    {
      key: "rule",
      label: "积分规则",
      html: info?.activityRule || "暂无积分规则",
      attachments: [],
    },
  ], [info]);

  if (!info) {
    return <div className="sale-activity-page"><Empty description="暂无审批信息" /></div>;
  }

  return (
    <div className={`sale-activity-audit-page sale-activity-audit-page--${type}`}>
      <div className="sale-activity-learn-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>我的活动</Breadcrumb.Item>
          <Breadcrumb.Item>{type === "expert" ? "专家活动" : "学员活动"}</Breadcrumb.Item>
          <Breadcrumb.Item>{auditTitle}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Card className="sale-activity-audit-flow-card" bordered={false} title={auditTitle}>
        <Steps
          className="sale-activity-audit-steps"
          current={isPassed ? 3 : isRejected ? 2 : 1}
          status={isRejected ? "error" : "process"}
        >
          <Steps.Step title="开始" />
          <Steps.Step title="提交报名" description={submitTime || "--"} />
          <Steps.Step title="报名审批" description={auditTime || (isPending ? "等待审核" : "--")} />
          <Steps.Step title="结束" description={statusText || "--"} />
        </Steps>
        <div className="sale-activity-audit-status-row">
          <Space align="center" size={12}>
            <span className={`sale-activity-audit-status-icon ${isPassed ? "is-pass" : isRejected ? "is-reject" : ""}`}>
              <ResultIcon />
            </span>
            <span>{roleText}审批状态：</span>
            <strong className={`sale-activity-audit-status-text ${isPassed ? "is-pass" : isRejected ? "is-reject" : "is-pending"}`}>
              {statusText || "待审核"}
            </strong>
          </Space>
          {isRejected && rejectReason ? (
            <Alert className="sale-activity-audit-reject" type="error" showIcon message={`拒绝原因：${rejectReason}`} />
          ) : null}
        </div>
      </Card>

      <Card className="sale-activity-audit-main-card" bordered={false}>
        <div className="sale-activity-audit-title-row">
          <Space align="center" size={16}>
            <h2>{info.name}</h2>
            <Tag color="orange">活动状态：{info.activityLearnStatusText || "--"}</Tag>
          </Space>
        </div>
        <div className="sale-activity-audit-summary">
          <img src={info.pictureUrl || projectDefault} alt={info.name} />
          <Descriptions className="sale-activity-audit-descriptions" bordered column={1} size="small">
            <Descriptions.Item label="活动时间">
              {textOrDash(info.beginTimeText || info.beginTime)} 至 {textOrDash(info.endTimeText || info.endTime)}
            </Descriptions.Item>
            {currentRoleItems.map((item) => (
              <Descriptions.Item key={item.label} label={item.label}>
                <span className="sale-activity-audit-current-value">{item.value}</span>
              </Descriptions.Item>
            ))}
            {otherRoleItems.map((item) => (
              <Descriptions.Item key={item.label} label={item.label}>
                {item.value}
              </Descriptions.Item>
            ))}
            <Descriptions.Item label={`${roleText}审批状态`}>
              <Tag color={statusTagColor}>{statusText || "--"}</Tag>
            </Descriptions.Item>
          </Descriptions>
          <div className="sale-activity-audit-actions">
            <Button className="sale-activity-audit-actions__expert" icon={<UserOutlined />} disabled>
              专家报名
            </Button>
            <Button className="sale-activity-audit-actions__user" icon={<TeamOutlined />} disabled>
              学员报名
            </Button>
          </div>
        </div>
      </Card>

      <Card className="sale-activity-audit-content-card" bordered={false}>
        <Tabs defaultActiveKey="content">
          {contentTabs.map((item) => (
            <Tabs.TabPane key={item.key} tab={item.label}>
              <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: item.html }} />
              <AttachmentList list={item.attachments} />
            </Tabs.TabPane>
          ))}
        </Tabs>
      </Card>
    </div>
  );
}
